import React, { lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import AppShell from '@/components/layout/AppShell';

// Critical pages (loaded eagerly)
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import PublicWebsite from '@/pages/PublicWebsite';

// Lazy-loaded pages (loaded on demand for performance)
const MembersPage = lazy(() => import('@/pages/MembersPage'));
const AttendancePage = lazy(() => import('@/pages/AttendancePage'));
const GivingPage = lazy(() => import('@/pages/GivingPage'));
const BiblePage = lazy(() => import('@/pages/BiblePage'));
const MediaPage = lazy(() => import('@/pages/MediaPage'));
const EventsPage = lazy(() => import('@/pages/EventsPage'));
const GroupsPage = lazy(() => import('@/pages/GroupsPage'));
const AnnouncementsPage = lazy(() => import('@/pages/AnnouncementsPage'));
const PrayerRequestsPage = lazy(() => import('@/pages/PrayerRequestsPage'));
const TestimoniesPage = lazy(() => import('@/pages/TestimoniesPage'));
const ConsultationsPage = lazy(() => import('@/pages/ConsultationsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const AIAssistantPage = lazy(() => import('@/pages/AIAssistantPage'));
const SermonToolsPage = lazy(() => import('@/pages/SermonToolsPage'));
const VisitorsPage = lazy(() => import('@/pages/VisitorsPage'));
const FollowUpsPage = lazy(() => import('@/pages/FollowUpsPage'));
const PledgesPage = lazy(() => import('@/pages/PledgesPage'));
const VolunteersPage = lazy(() => import('@/pages/VolunteersPage'));
const FamiliesPage = lazy(() => import('@/pages/FamiliesPage'));
const ServicePlannerPage = lazy(() => import('@/pages/ServicePlannerPage'));
const MessagesPage = lazy(() => import('@/pages/MessagesPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const MinistriesPage = lazy(() => import('@/pages/MinistriesPage'));
const AssetsPage = lazy(() => import('@/pages/AssetsPage'));
const AppointmentsPage = lazy(() => import('@/pages/AppointmentsPage'));
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'));
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'));
const OnlineGivingPage = lazy(() => import('@/pages/OnlineGivingPage'));
const LiveStreamPage = lazy(() => import('@/pages/LiveStreamPage'));
const ReportsBuilderPage = lazy(() => import('@/pages/ReportsBuilderPage'));
const AutomationPage = lazy(() => import('@/pages/AutomationPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('@/pages/PaymentCancelPage'));
const DonorAnalyticsPage = lazy(() => import('@/pages/DonorAnalyticsPage'));
const EngagementScoresPage = lazy(() => import('@/pages/EngagementScoresPage'));
const CheckInPage = lazy(() => import('@/pages/CheckInPage'));
const AIAnalyticsPage = lazy(() => import('@/pages/AIAnalyticsPage'));
const MemberPortalPage = lazy(() => import('@/pages/MemberPortalPage'));
const PastorPortalPage = lazy(() => import('@/pages/PastorPortalPage'));
const LeaderPortalPage = lazy(() => import('@/pages/LeaderPortalPage'));
const AdminPortalPage = lazy(() => import('@/pages/AdminPortalPage'));
const IntegrationMarketplacePage = lazy(() => import('@/pages/IntegrationMarketplacePage'));
const ProphecyPage = lazy(() => import('@/pages/ProphecyPage'));
const SpiritualGrowthPage = lazy(() => import('@/pages/SpiritualGrowthPage'));
const ForumPage = lazy(() => import('@/pages/ForumPage'));
const AdminToolsPage = lazy(() => import('@/pages/AdminToolsPage'));
const CMSManagementPage = lazy(() => import('@/pages/CMSManagementPage'));
const PodcastPage = lazy(() => import('@/pages/PodcastPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const PushNotificationsPage = lazy(() => import('@/pages/PushNotificationsPage'));

// Protected Route wrapper
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (roles && !roles.includes(user.role)) {
    return <Navigate to={getRolePortal(user.role)} replace />;
  }

  return <AppShell>{children}</AppShell>;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={getRolePortal(user.role)} replace />;
  }
  return children;
};

function getRolePortal(role) {
  switch (role) {
    case 'super_admin': return '/dashboard';
    case 'admin': return '/admin-portal';
    case 'pastor': return '/pastor-portal';
    case 'leader': return '/leader-portal';
    case 'member': return '/my-portal';
    default: return '/my-portal';
  }
}

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[300px]">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="sanctuary-theme">
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/" element={<PublicWebsite />} />
          <Route path="/church" element={<PublicWebsite />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/staff-login" element={<PublicRoute><LoginPage mode="staff" /></PublicRoute>} />

          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/members" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><MembersPage /></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><AttendancePage /></ProtectedRoute>} />
          <Route path="/giving" element={<ProtectedRoute><GivingPage /></ProtectedRoute>} />
          <Route path="/bible" element={<ProtectedRoute><BiblePage /></ProtectedRoute>} />
          <Route path="/media" element={<ProtectedRoute><MediaPage /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
          <Route path="/groups" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><GroupsPage /></ProtectedRoute>} />
          <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
          <Route path="/prayer-requests" element={<ProtectedRoute><PrayerRequestsPage /></ProtectedRoute>} />
          <Route path="/testimonies" element={<ProtectedRoute><TestimoniesPage /></ProtectedRoute>} />
          <Route path="/consultations" element={<ProtectedRoute><ConsultationsPage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><ReportsPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute roles={['super_admin']}><SettingsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

          {/* Enterprise routes */}
          <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
          <Route path="/sermon-tools" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><SermonToolsPage /></ProtectedRoute>} />
          <Route path="/visitors" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><VisitorsPage /></ProtectedRoute>} />
          <Route path="/follow-ups" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><FollowUpsPage /></ProtectedRoute>} />
          <Route path="/pledges" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><PledgesPage /></ProtectedRoute>} />
          <Route path="/volunteers" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><VolunteersPage /></ProtectedRoute>} />
          <Route path="/families" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><FamiliesPage /></ProtectedRoute>} />
          <Route path="/service-planner" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><ServicePlannerPage /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

          {/* New management routes */}
          <Route path="/users" element={<ProtectedRoute roles={['super_admin', 'admin']}><UsersPage /></ProtectedRoute>} />
          <Route path="/ministries" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><MinistriesPage /></ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute roles={['super_admin', 'admin']}><AssetsPage /></ProtectedRoute>} />
          <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><CampaignsPage /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />

          {/* Game-changer routes */}
          <Route path="/online-giving" element={<ProtectedRoute><OnlineGivingPage /></ProtectedRoute>} />
          <Route path="/giving/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
          <Route path="/giving/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
          <Route path="/live-stream" element={<ProtectedRoute><LiveStreamPage /></ProtectedRoute>} />
          <Route path="/reports-builder" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><ReportsBuilderPage /></ProtectedRoute>} />
          <Route path="/automation" element={<ProtectedRoute roles={['super_admin', 'admin']}><AutomationPage /></ProtectedRoute>} />
          <Route path="/donor-analytics" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><DonorAnalyticsPage /></ProtectedRoute>} />
          <Route path="/engagement" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><EngagementScoresPage /></ProtectedRoute>} />
          <Route path="/check-in" element={<ProtectedRoute><CheckInPage /></ProtectedRoute>} />
          <Route path="/ai-analytics" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><AIAnalyticsPage /></ProtectedRoute>} />
          <Route path="/my-portal" element={<ProtectedRoute><MemberPortalPage /></ProtectedRoute>} />
          <Route path="/pastor-portal" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor']}><PastorPortalPage /></ProtectedRoute>} />
          <Route path="/leader-portal" element={<ProtectedRoute roles={['super_admin', 'admin', 'pastor', 'leader']}><LeaderPortalPage /></ProtectedRoute>} />
          <Route path="/admin-portal" element={<ProtectedRoute roles={['super_admin', 'admin']}><AdminPortalPage /></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute roles={['super_admin', 'admin']}><IntegrationMarketplacePage /></ProtectedRoute>} />
          <Route path="/prophecy" element={<ProtectedRoute><ProphecyPage /></ProtectedRoute>} />
          <Route path="/spiritual-growth" element={<ProtectedRoute><SpiritualGrowthPage /></ProtectedRoute>} />
          <Route path="/forum" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
          <Route path="/admin-tools" element={<ProtectedRoute roles={['super_admin', 'admin']}><AdminToolsPage /></ProtectedRoute>} />
          <Route path="/website-cms" element={<ProtectedRoute roles={['super_admin']}><CMSManagementPage /></ProtectedRoute>} />
          <Route path="/podcasts" element={<ProtectedRoute><PodcastPage /></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/push" element={<ProtectedRoute roles={['super_admin', 'admin']}><PushNotificationsPage /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
